import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { fmtDate } from '../utils/helpers';
import { useAuth } from '../App';

function SquawkBadge({ status }) {
  const map = {
    open:        ['badge-open',     'Open'],
    in_progress: ['badge-progress', 'In Progress'],
    resolved:    ['badge-resolved', 'Resolved'],
  };
  const [cls, label] = map[status] || ['badge-open', status || 'Open'];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function ManageModal({ squawk, onClose, onSaved }) {
  const [form, setForm] = useState({
    squawk_status:         squawk.squawk_status || 'open',
    squawk_progress_notes: squawk.squawk_progress_notes || '',
    squawk_resolution:     squawk.squawk_resolution || '',
  });
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    setBusy(true);
    setError('');
    try {
      await api.sessions.manageSquawk(squawk.id, form);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  };
  const modal = {
    background: '#fff', borderRadius: 8, padding: 28, width: 520,
    maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
  };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{fontWeight:700, fontSize:16, marginBottom:16, color:'#1A2E5A'}}>Manage Squawk</div>

        {/* Session context */}
        <div style={{background:'#f4f6fa', borderRadius:6, padding:'10px 14px', marginBottom:18, fontSize:13}}>
          <div><strong>Date:</strong> {fmtDate(squawk.date)} &nbsp; <strong>Instructor:</strong> {squawk.instructor_name}</div>
          {squawk.pic_name && <div><strong>PIC:</strong> {squawk.pic_name}{squawk.sic_name ? ` &nbsp; SIC: ${squawk.sic_name}` : ''}</div>}
          <div><strong>Training:</strong> {squawk.training_type}</div>
          <div style={{marginTop:6, fontStyle:'italic', color:'#555'}}>{squawk.squawk}</div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-group" style={{marginBottom:12}}>
          <label>Status</label>
          <select value={form.squawk_status} onChange={e => set('squawk_status', e.target.value)}>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>

        {(form.squawk_status === 'in_progress' || form.squawk_status === 'resolved') && (
          <div className="form-group" style={{marginBottom:12}}>
            <label>Progress Notes</label>
            <textarea
              rows={2} value={form.squawk_progress_notes}
              placeholder="Work performed, parts ordered, maintenance in progress..."
              onChange={e => set('squawk_progress_notes', e.target.value)}
            />
          </div>
        )}

        {form.squawk_status === 'resolved' && (
          <div className="form-group" style={{marginBottom:12}}>
            <label>Resolution Notes</label>
            <textarea
              rows={2} value={form.squawk_resolution}
              placeholder="How was the issue resolved?"
              onChange={e => set('squawk_resolution', e.target.value)}
            />
          </div>
        )}

        <div className="btn-row" style={{marginTop:20}}>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving...' : 'Save'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function ReportSquawk() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [data,       setData]       = useState(null);
  const [filters,    setFilters]    = useState({ status: '' });
  const [loading,    setLoading]    = useState(false);
  const [managing,   setManaging]   = useState(null);

  useEffect(() => { load(); }, [filters]);

  async function load() {
    setLoading(true);
    try { setData(await api.reports.squawk(filters)); } catch {}
    setLoading(false);
  }

  function setF(k, v) { setFilters(f => ({ ...f, [k]: v })); }

  return (
    <div>
      <div className="section-title">Squawk / Maintenance Report</div>

      <div className="filter-bar">
        <div className="form-group">
          <label>From</label>
          <input type="date" value={filters.from || ''} onChange={e => setF('from', e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" value={filters.to || ''} onChange={e => setF('to', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Status</label>
          <select value={filters.status} onChange={e => setF('status', e.target.value)}>
            <option value="">All Squawks</option>
            <option value="open">Open Only</option>
            <option value="progress">In Progress</option>
            <option value="resolved">Resolved Only</option>
          </select>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()}>Print / PDF</button>
      </div>

      {loading && <div className="empty">Loading...</div>}

      {data && (
        <>
          <div className="stat-grid" style={{gridTemplateColumns:'repeat(4,1fr)', marginBottom:20}}>
            <div className="stat-card">
              <div className="stat-value">{data.stats.total}</div>
              <div className="stat-label">Total Squawks</div>
            </div>
            <div className="stat-card red">
              <div className="stat-value">{data.stats.open}</div>
              <div className="stat-label">Open</div>
            </div>
            <div className="stat-card" style={{borderTop:'3px solid var(--warning)'}}>
              <div className="stat-value" style={{color:'var(--warning)'}}>{data.stats.in_progress}</div>
              <div className="stat-label">In Progress</div>
            </div>
            <div className="stat-card green">
              <div className="stat-value">{data.stats.resolved}</div>
              <div className="stat-label">Resolved</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Squawk Log</h2>
              <p>Open → In Progress → Resolved</p>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {data.squawks.length === 0 ? (
                <div className="empty"><div className="empty-icon">✅</div>No squawks found</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Instructor</th>
                        <th>Crew</th>
                        <th>Training</th>
                        <th>Squawk Description</th>
                        <th>Status</th>
                        <th>Notes</th>
                        {isAdmin && <th>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {data.squawks.map(r => (
                        <tr key={r.id} style={r.squawk_status === 'open' ? {background:'#fff8f8'} : r.squawk_status === 'in_progress' ? {background:'#fffbf0'} : {}}>
                          <td>{fmtDate(r.date)}</td>
                          <td>{r.instructor_name}</td>
                          <td>
                            {r.pic_name && <div style={{fontWeight:600, fontSize:12}}>PIC: {r.pic_name}</div>}
                            {r.sic_name && <div style={{fontSize:11, color:'#555'}}>SIC: {r.sic_name}</div>}
                            {!r.pic_name && <span style={{color:'#aaa'}}>—</span>}
                          </td>
                          <td><span className="badge badge-type">{r.training_type}</span></td>
                          <td style={{maxWidth:220, wordWrap:'break-word'}}>{r.squawk}</td>
                          <td><SquawkBadge status={r.squawk_status} /></td>
                          <td style={{fontSize:12, color:'#555', maxWidth:180, wordWrap:'break-word'}}>
                            {r.squawk_progress_notes && (
                              <div><strong>Progress:</strong> {r.squawk_progress_notes}</div>
                            )}
                            {r.squawk_resolution && (
                              <div style={{marginTop:r.squawk_progress_notes ? 4 : 0}}>
                                <strong>Resolution:</strong> {r.squawk_resolution}
                              </div>
                            )}
                            {!r.squawk_progress_notes && !r.squawk_resolution && <span style={{color:'#aaa'}}>—</span>}
                          </td>
                          {isAdmin && (
                            <td>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setManaging(r)}
                              >
                                Manage
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {managing && (
        <ManageModal
          squawk={managing}
          onClose={() => setManaging(null)}
          onSaved={() => { setManaging(null); load(); }}
        />
      )}
    </div>
  );
}
